/**
 * SSE Event Handler
 * 
 * Receives a parsed SSE event and dispatches it to the appropriate managers
 * based on the serverEventData configuration.
 * 
 * Logic:
 *   - Looks up the event type in serverEventData
 *   - For each action, checks exactUserId to determine if it applies to this user
 *   - Dispatches to notificationManager (for 'notify' actions)
 *   - Dispatches to refreshManager (for 'refresh' actions)
 */

import serverEventData from '../../model/events/serverEventData.js';
import { notificationManager } from './notificationManager.js';
import { refreshManager } from './refreshManager.js';
import { nSerieManager } from './nSerieManager.js';
import { onModelDeploy } from '../../model/loadModel.js';

/**
 * Handle an incoming SSE event
 * @param {object} event - The parsed SSE event payload
 * @param {string} event.type - Event type identifier (e.g. 'creditNote.match')
 * @param {string} event.orgId - Organization ID
 * @param {string} event.userId - User who triggered the operation
 * @param {string} [event.message] - Translation key for the message
 * @param {object} [event.payload] - Optional result data
 * @param {number} event.timestamp - Unix timestamp
 * @param {string} currentUserId - The currently authenticated user's ID
 * @param {import('@tanstack/react-query').QueryClient} queryClient - React Query client instance
 */
const handleSSEEvent = (event, currentUserId, queryClient) => {
    // Skip internal connection events
    if (event.type === 'connected') {
        console.log('[SSE EventHandler] Connection confirmed:', event);
        return;
    }

    const config = serverEventData[event.type];

    if (!config) {
        console.log(`[SSE EventHandler] No config found for event type: ${event.type}`);
        return;
    }

    console.log(`[SSE EventHandler] Handling event: ${event.type}, triggeredBy: ${event.userId}, currentUser: ${currentUserId}`);

    const isCurrentUser = event.userId === currentUserId;

    for (const action of config.actions) {
        // Determine if this action applies to the current user
        const shouldExecute = !action.exactUserId || isCurrentUser;

        if (!shouldExecute) {
            console.log(`[SSE EventHandler] Skipping action '${action.type}' for event '${event.type}' (exactUserId=true, not the triggering user)`);
            continue;
        }

        if (action.type === 'notify') {
            /*
             * `values` names payload fields to fill the message's ${1}, ${2}...
             * placeholders, in order. It is how an event reports what it did —
             * "142 applied, 3 conflicts" rather than "an import finished" — and
             * it stays out of the translation layer, which only ever sees keys.
             */
            const { values, ...notification } = action.notification;

            notificationManager.show({
                ...notification,
                ...(values?.length
                    ? { values: values.map(field => event.payload?.[field] ?? 0) }
                    : {})
            });
        }

        if (action.type === 'refresh') {
            /*
             * Some events do not know at declaration time which list they
             * touched — a generic import applies to whichever entity was
             * imported — so the endpoint can be read off the payload instead of
             * being written here.
             */
            const fromPayload = action.queryParamsFromPayload
                ? [event.payload?.[action.queryParamsFromPayload]].filter(Boolean)
                : [];

            refreshManager.invalidate(queryClient, [...(action.queryParams || []), ...fromPayload]);
        }

        if (action.type === 'model') {
            // The broadcast's message is { modelVersion, previousModelVersion, changedNamespaces }.
            onModelDeploy(event.message);
        }

        if (action.type === 'nSerie') {
            nSerieManager.advance(queryClient, event.payload);
        }
    }
};

export default handleSSEEvent;
